import { BooleanField, BooleanInput, SimpleShowLayout, TextField, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography, FormControlLabel, Checkbox, IconButton, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useWatch, useFormContext } from 'react-hook-form';
import { useMemo, useEffect } from 'react';
import CopyIcon from '@/assets/icons/copy-icon.svg?react';
import { CustomTimePicker } from '@/components/global/inputs/TimePicker';
import { allDayTime } from '@/constants/Date';
import { Palette } from '@/themes/themeOverrides/Palette';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const StyledWorkingDayContainer = ({ children, hasError, ...props }) => (
    <Box
        sx={{
            position: 'relative',
            width: 'calc(100% - 48px)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: Palette.gray.gray75,
            borderRadius: '16px',
            gap: '32px',
            marginBottom: hasError ? '48px' : '0',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                width: '100%',
                padding: '20px',
                gap: '11px',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
                backgroundColor: Palette.gray.gray75,
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused': {
                backgroundColor: Palette.white,
            },
            '& .MuiFormLabel-root.MuiInputLabel-root': {
                transform: 'translate(14px, -11px) scale(1)',
                backgroundColor: Palette.gray.gray75,
            },
            '& .MuiInputBase-input.Mui-disabled': {
                fontWeight: '400 !important',
            },
            '& .MuiFormControl-root.ra-input': {
                position: 'relative',
            },
            '& .MuiFormHelperText-root': {
                display: 'none',
            },
            '& .copy-button': {
                position: 'absolute',
                right: '-16px',
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: '32px',
                width: '32px',
                height: '32px',
                borderRadius: '10px',
                backgroundColor: Palette.gray.gray150,
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    right: '20px',
                },
                '&:hover': {
                    backgroundColor: Palette.gray.gray100 + ' !important',
                    outline: `2px solid ${Palette.gray.gray300}`,
                    outlineOffset: '-2px',
                },
                '& .MuiButton-startIcon': {
                    margin: 0,
                },
            },
            '& .time-inputs': {
                display: 'flex',
                gap: '8px',
                '& .MuiFormControl-root.ra-input': {
                    margin: '0',
                },
            },
            '& .all-day-checkbox': {
                marginLeft: '0px',
            },
        }}
        {...props}
    >
        {children}
    </Box>
);

const StyledCustomErrorMessage = ({ children, ...props }) => (
    <Box
        sx={{
            position: 'absolute',
            bottom: '-46px',
            left: '0',
            width: 'calc(100% - 40px)',
            padding: '12px 20px',
            backgroundColor: Palette.red.red50,
            borderRadius: '16px',
            fontSize: '14px',
            fontWeight: '400',
            zIndex: 1,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                width: '100%',
            },
        }}
        {...props}
    >
        {children}
    </Box>
);

const StyledDayLabel = ({ children, ...props }) => (
    <Typography
        variant='caption'
        sx={{
            fontSize: '14px',
            fontWeight: '600',
            width: '40px',
            marginRight: '12px',
        }}
        {...props}
    >
        {children}
    </Typography>
);

const StyledBooleanInput = ({ source, name, ...props }) => {
    const translate = useTranslate();
    return (
        <BooleanInput
            label={translate('resources.collectionPoint.form.open')}
            source={source}
            name={name}
            parse={value => !value}
            format={value => !value}
            defaultValue={true}
            fullWidth
            size='small'
            {...props}
        />
    );
};

const StyledCheckbox = ({ checked, onChange, disabled, ...props }) => (
    <FormControlLabel
        control={<Checkbox checked={checked} onChange={onChange} disabled={disabled} />}
        label='24h'
        sx={{
            '& .MuiButtonBase-root.MuiCheckbox-root': {
                padding: '8px',
            },
            '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                fontWeight: '400',
            },
        }}
        {...props}
    />
);

const useWorkingDayState = (dayShortName, subform, record) => {
    const sourcePrefix = subform ? 'collectionPoint.' : '';

    const fieldNames = useMemo(
        () => ({
            from: `${sourcePrefix}${dayShortName}From`,
            to: `${sourcePrefix}${dayShortName}To`,
            closed: `${sourcePrefix}${dayShortName}Closed`,
        }),
        [sourcePrefix, dayShortName],
    );

    const isInactive = useWatch({
        name: fieldNames.closed,
        defaultValue: record?.[fieldNames.closed] || false,
    });

    return { fieldNames, isInactive };
};

export const WorkingDayGroupField = ({ dayShortName, dayLongName }) => {
    const fieldNames = {
        from: `${dayShortName}From`,
        to: `${dayShortName}To`,
        closed: `${dayShortName}Closed`,
    };

    const labels = {
        from: `${dayLongName} from`,
        to: `${dayLongName} to`,
        closed: `${dayLongName} closed`,
    };

    return (
        <Box display='flex'>
            <SimpleShowLayout flex={1}>
                <TextField source={fieldNames.from} label={labels.from} />
            </SimpleShowLayout>
            <SimpleShowLayout flex={1}>
                <TextField source={fieldNames.to} label={labels.to} />
            </SimpleShowLayout>
            <SimpleShowLayout flex={1}>
                <BooleanField source={fieldNames.closed} label={labels.closed} />
            </SimpleShowLayout>
        </Box>
    );
};

export const WorkingDayGroupInput = ({ dayName, dayShortName, subform }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { setValue, getValues } = useFormContext();
    const { fieldNames, isInactive } = useWorkingDayState(dayShortName, subform, record);

    const fromVal = useWatch({ name: fieldNames.from, defaultValue: '' });
    const toVal = useWatch({ name: fieldNames.to, defaultValue: '' });
    const is24h = !isInactive && fromVal === allDayTime.from && toVal === allDayTime.to;

    const hasError = !isInactive && (!fromVal || !toVal);

    const handle24hChange = (event, checked) => {
        if (checked) {
            setValue(fieldNames.from, allDayTime.from, { shouldDirty: true, shouldValidate: true });
            setValue(fieldNames.to, allDayTime.to, { shouldDirty: true, shouldValidate: true });
        } else if (fromVal === allDayTime.from && toVal === allDayTime.to) {
            setValue(fieldNames.from, '', { shouldDirty: true, shouldValidate: true });
            setValue(fieldNames.to, '', { shouldDirty: true, shouldValidate: true });
        }
    };

    useEffect(() => {
        if (isInactive) {
            setValue(fieldNames.from, null);
            setValue(fieldNames.to, null);
        }
    }, [isInactive, setValue, fieldNames.from, fieldNames.to]);

    const handleCopyBelow = () => {
        const currentValues = {
            from: getValues(fieldNames.from),
            to: getValues(fieldNames.to),
            closed: getValues(fieldNames.closed),
        };

        const allDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

        const currentDayIndex = allDays.indexOf(dayShortName);

        const nextDayIndex = currentDayIndex + 1;
        if (nextDayIndex < allDays.length) {
            const targetDay = allDays[nextDayIndex];
            const targetPrefix = subform ? 'collectionPoint.' : '';
            const targetFieldNames = {
                from: `${targetPrefix}${targetDay}From`,
                to: `${targetPrefix}${targetDay}To`,
                closed: `${targetPrefix}${targetDay}Closed`,
            };

            setValue(targetFieldNames.from, currentValues.from, { shouldDirty: true, shouldValidate: true });
            setValue(targetFieldNames.to, currentValues.to, { shouldDirty: true, shouldValidate: true });
            setValue(targetFieldNames.closed, currentValues.closed, { shouldDirty: true, shouldValidate: true });
        }
    };

    return (
        <StyledWorkingDayContainer hasError={hasError}>
            <StyledDayLabel>{dayName}</StyledDayLabel>

            <Box>
                <StyledBooleanInput source={fieldNames.closed} name={fieldNames.closed} size='x-small' />
            </Box>

            <Box className='time-inputs'>
                <Box className='time-picker' sx={{ position: 'relative' }}>
                    <span className='range-picker-label'>{translate('resources.adsCampaign.labels.from')}</span>
                    <CustomTimePicker
                        value={fromVal}
                        disabled={isInactive}
                        onChange={value => setValue(fieldNames.from, value, { shouldDirty: true, shouldValidate: true })}
                    />
                </Box>
                <Box className='time-picker' sx={{ position: 'relative' }}>
                    <span className='range-picker-label'>{translate('resources.adsCampaign.labels.to')}</span>
                    <CustomTimePicker
                        value={toVal}
                        disabled={isInactive}
                        onChange={value => setValue(fieldNames.to, value, { shouldDirty: true, shouldValidate: true })}
                    />
                </Box>
                <StyledCheckbox className='all-day-checkbox' checked={is24h} onChange={handle24hChange} disabled={isInactive} />
            </Box>
            <Tooltip title={translate('common.copyBelow')}>
                <IconButton className='copy-button' onClick={handleCopyBelow}>
                    <CopyIcon />
                </IconButton>
            </Tooltip>
            {hasError && <StyledCustomErrorMessage>{translate('common.validation.hoursRequired')}</StyledCustomErrorMessage>}
        </StyledWorkingDayContainer>
    );
};

WorkingDayGroupField.propTypes = {
    dayShortName: PropTypes.string.isRequired,
    dayLongName: PropTypes.string.isRequired,
};

WorkingDayGroupInput.propTypes = {
    dayName: PropTypes.string.isRequired,
    dayShortName: PropTypes.string.isRequired,
    subform: PropTypes.bool,
};
